import { useDialogController, useViewport } from '@amx/ux-twoui';
import { Persona as FluentUIPersona, FontIcon, PersonaSize } from '@fluentui/react';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import React, { useCallback } from 'react';
import { settingsManager } from '../../shared/settings';
import { useGetProfilePhotoAllowedOperations } from '../../hooks/useUpdateProfilePhoto';
import { getViewportTypeIndex } from '../../shared/utils';
import { useGetUserPhoto } from '../../hooks/useGetUserPhoto';
import { EditPhotoDialog } from '../../shared/EditProfilePhoto/EditPhotoDialog';
import { isFeatureEnabled } from '../../shared/SettingsManager';

interface IPersonaProps {
  displayName?: string;
  tenantId?: string;
}

const accountStyles = require('./PersonBadge.scoped.scss');

export const Persona: React.FC<IPersonaProps> = ({ displayName, tenantId }) => {
  const allowedOperationsQuery = useGetProfilePhotoAllowedOperations();
  const getUserPhotoQuery = useGetUserPhoto();

  const dialogController = useDialogController();
  const [t] = useLocalization(['Account']);
  const viewportType = useViewport();

  const onPersonaClick = useCallback(() => {
    dialogController.openDialog(
      <EditPhotoDialog
        displayName={displayName || ''}
        photoUrl={getUserPhotoQuery.data}
        allowedOperations={allowedOperationsQuery.data}
      />,
    );
  }, [allowedOperationsQuery.data, dialogController, displayName, getUserPhotoQuery.data]);

  const personaSize =
    getViewportTypeIndex(viewportType) <= getViewportTypeIndex('s')
      ? PersonaSize.extraSmall
      : getViewportTypeIndex(viewportType) <= getViewportTypeIndex('m')
      ? PersonaSize.small
      : PersonaSize.extraLarge;

  const photoEditorSettings = settingsManager.get('photoEditorEnabled');
  const photoEditorEnabled = isFeatureEnabled(photoEditorSettings, tenantId) && allowedOperationsQuery.data;
  return (
    <div className={accountStyles.personaFlex}>
      {photoEditorEnabled ? (
        <button className={accountStyles.editablePersonaContainer} onClick={onPersonaClick}>
          <FluentUIPersona
            id={'profilePhoto'}
            text={displayName}
            hidePersonaDetails={true}
            size={personaSize}
            imageUrl={getUserPhotoQuery.data}
            title={t('editablePhotoTooltip')}
            className={accountStyles.editablePersona}
            imageAlt={t('profilePhoto')}
          />
          {
            <FontIcon
              iconName="Edit"
              className={
                personaSize === PersonaSize.extraSmall
                  ? accountStyles.presenceCircleXSmall
                  : personaSize === PersonaSize.small
                  ? accountStyles.presenceCircleSmall
                  : accountStyles.presenceCircle
              }
            />
          }
        </button>
      ) : (
        <FluentUIPersona
          id={'profilePhoto'}
          text={displayName}
          hidePersonaDetails={true}
          size={personaSize}
          imageUrl={getUserPhotoQuery.data}
          title={t('uneditablePhotoTooltip')}
          imageAlt={t('profilePhoto')}
        />
      )}
    </div>
  );
};
